import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { errorMessage, financeGet, todayValue, useFinanceCodes, type FinanceCtx, type FinanceMember } from '../financeApi';
import ImportReview, { type ReviewRow } from './ImportReview';
import { cleanName, loadExistingKeys, matchMember, readSheet, recordKey, toAmount, toDateValue } from './importUtils';

const COLUMNS = ['날짜', '이름', '헌금종류', '금액', '방법', '메모'];

// 주일 봉투 집계표를 엑셀로 올려 한 번에 입력. 양식의 열 이름(날짜·이름·헌금종류·금액)으로 읽고,
// 이름은 교적과 자동 매칭한 뒤 확인 화면(ImportReview)에서 고쳐서 저장한다.
export default function IncomeBulkUpload({ ctx, onDone }: { ctx: FinanceCtx; onDone: () => void }) {
  const { incomeCategories } = useFinanceCodes(ctx);
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const downloadTemplate = () => {
    const today = todayValue();
    const ws = XLSX.utils.aoa_to_sheet([
      COLUMNS,
      [today, '홍길동', '십일조', 100000, '현금', ''],
      [today, '김성도', '감사헌금', 50000, '계좌이체', '생일 감사'],
      [today, '무명', '주일헌금', 30000, '현금', ''],
    ]);
    ws['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '헌금 집계');
    XLSX.writeFile(wb, '헌금_일괄입력_양식.xlsx');
  };

  const onFile = async (file: File | undefined) => {
    setError(null);
    setDone(null);
    setRows(null);
    if (!file) return;
    setBusy(true);
    try {
      const sheet = await readSheet(file);
      const col = (name: string) => sheet.headers.findIndex((h) => h.replace(/\s/g, '') === name);
      const idx = { date: col('날짜'), name: col('이름'), category: col('헌금종류'), amount: col('금액'), method: col('방법'), memo: col('메모') };
      if (idx.date < 0 || idx.name < 0 || idx.category < 0 || idx.amount < 0) {
        setError('양식의 열 이름(날짜·이름·헌금종류·금액)을 찾지 못했습니다. "양식 다운로드"로 받은 파일을 사용해 주세요.');
        return;
      }
      if (sheet.rows.length === 0) {
        setError('입력된 줄이 없습니다.');
        return;
      }
      if (sheet.rows.length > 3000) {
        setError('한 번에 3,000줄까지 올릴 수 있습니다.');
        return;
      }
      const members = (await financeGet<FinanceMember[]>(ctx, '/members')) ?? [];
      const parsed = sheet.rows.map((cells, i) => {
        const rawName = String(cells[idx.name] ?? '').trim();
        const { match, candidates } = matchMember(rawName, members);
        const category = String(cells[idx.category] ?? '').trim();
        const row: ReviewRow = {
          key: i,
          include: true,
          recordDate: toDateValue(cells[idx.date]),
          rawName,
          match,
          candidates,
          category: category || incomeCategories[0] || '',
          amount: toAmount(cells[idx.amount]),
          memo: idx.memo >= 0 ? String(cells[idx.memo] ?? '').trim() : '',
          method: idx.method >= 0 ? String(cells[idx.method] ?? '').trim() : '',
          duplicate: false,
        };
        return row;
      });
      const existing = await loadExistingKeys(ctx, parsed.map((r) => r.recordDate));
      setRows(parsed.map((r) => {
        const duplicate = existing.has(recordKey(r.recordDate, r.amount, r.match.kind === 'member' ? r.match.name : cleanName(r.rawName)));
        return { ...r, duplicate, include: !duplicate };
      }));
    } catch (err) {
      setError(errorMessage(err, '파일을 읽지 못했습니다. 엑셀(.xlsx, .xls) 또는 CSV 파일인지 확인해 주세요.'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">엑셀 일괄 입력</h2>
          <p className="manage-panel-desc">주일 봉투 집계표를 엑셀로 올려 여러 건의 헌금을 한 번에 입력합니다.</p>
        </div>
      </div>

      {done !== null && (
        <div className="fin-inline-ok">
          {done}건을 저장했습니다. <button type="button" className="fin-linkbtn" onClick={onDone}>헌금 내역 보기 →</button>
        </div>
      )}

      {!rows && (
        <div className="fin-steps">
          <div className="fin-step">
            <b>1. 양식 받기</b>
            <p>열: {COLUMNS.join(' · ')}. 헌금종류는 재정 설정의 헌금 항목 이름과 같게 적어 주세요. 이름이 '무명'이면 무명 헌금으로 처리됩니다.</p>
            <button type="button" className="manage-btn" onClick={downloadTemplate}>양식 다운로드</button>
          </div>
          <div className="fin-step">
            <b>2. 파일 올리기</b>
            <p>이름은 교적(성도 명부)과 자동으로 매칭됩니다. 동명이인·미등록 이름은 다음 화면에서 직접 고를 수 있습니다.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => onFile(e.target.files?.[0])} />
            <button type="button" className="manage-btn manage-btn--primary" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? '읽는 중...' : '엑셀 파일 선택'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="fin-inline-error">{error}</div>}

      {rows && (
        <ImportReview
          ctx={ctx}
          initialRows={rows}
          categories={incomeCategories}
          source="excel"
          onCancel={() => setRows(null)}
          onSaved={(count) => {
            setRows(null);
            setDone(count);
          }}
        />
      )}
    </div>
  );
}
